import {DrupalNode, DrupalTaxonomyTerm} from 'next-drupal';
import {
  Dictionary,
  DrupalProductNode,
  ProductTypeTaxonomyTerm,
  TestingMethodTerm
} from "@/types/hygiena-types";
import {RefObject, useContext} from "react";
import classNames from "classnames";
import dynamic from "next/dynamic";
import {DictionaryContext} from "@/context/dictionary-context";
import {TrainingVideosSlider} from "@/components/organisms/slider";
import {ModalContext} from "@/context/modal-context";
import {WidgetFaqTerms} from "@/components/organisms/widget--faq-terms";
import {getParagraphSections} from "@/lib/get-paragraph-sections";
import {RenderSections} from "@/components/templates/sections/section-regions";

const ArticleSlider = dynamic(() => import("../../../organisms/slider").then((mod) => mod.ArticleSlider));
const ProductSlider = dynamic(() => import("../../../organisms/slider").then((mod) => mod.ProductSlider));
const DynamicProductGrid = dynamic(() => import("../../../organisms/widget--product-grid").then((mod) => mod.DynamicProductGrid));
const NodeProduct = dynamic(() => import("../../nodes/node--product").then((mod) => mod.NodeProduct));
const FormattedText = dynamic(() => import("../../../formatted-text").then((mod) => mod.FormattedText));
const MissingProduct = dynamic(() => import("../../../molecules/missing-product").then((mod) => mod.MissingProduct));
const Heading = dynamic(() => import("../../../atoms/heading").then((mod) => mod.Heading));
const Body = dynamic(() => import("../../../atoms/body").then((mod) => mod.Body));
const SectionHeading = dynamic(() => import("../../../organisms/section-heading"));
const TaxonomyProductTypeTeaser = dynamic(() => import("../../../templates/taxonomy/taxonomy--product_type--teaser").then((mod) => mod.TaxonomyProductTypeTeaser));

/**
 * The Product Type output that is to be imported using dynamic().
 *
 * @todo products are being passed in via a reference from the term,
 *   Search API may need to be leveraged if faceting becomes complex.
 *
 * @param taxonomy_term
 * @param additionalContent
 * @param solutionsRef
 * @constructor
 */
export function ContentProductType({
                                     taxonomy_term,
                                     additionalContent,
                                     solutionsRef,
                                   }: {
  taxonomy_term: ProductTypeTaxonomyTerm;
  solutionsRef: RefObject<any>,
  additionalContent?: {
    subtypes: DrupalTaxonomyTerm[],
    products: DrupalProductNode[],
    childTerms: DrupalTaxonomyTerm[],
    relatedNews: DrupalNode[],
    relatedProducts?: DrupalProductNode[],
    complementaryProducts?: DrupalProductNode[],
  };
}) {
  let products = additionalContent?.products ?? [];
  const testingMethods = taxonomy_term?.field_product_type_show_test_met
    ? getTermProductTestingMethods() : [];
  const childTerms = additionalContent?.childTerms?.filter(item => item?.path?.alias);
  const productCount = additionalContent?.products?.length ? additionalContent?.products?.length : 0;
  const t = useContext<Dictionary>(DictionaryContext);
  const listingTitleText = taxonomy_term?.field_product_type_listing_title ?? `${t?.all?.testing_solutions}`;
  const childTermsCount = childTerms?.length;
  const {setOpenModal, setOpenSmallModal, setOpenModalContent, setOpenSmallModalContent} = useContext(ModalContext);
  const {sections, sectionMap} = getParagraphSections(taxonomy_term?.field_product_type_components);

  /**
   * Get a unique list of testing methods from the products, sorted by term weight.
   */
  function getTermProductTestingMethods(): TestingMethodTerm[] {
    const added: string[] = [];
    const testingMethods: TestingMethodTerm[] = [];
    for (const node of additionalContent?.products ?? []) {
      const testingMethod = node?.field_product_testing_method;
      if (testingMethod) {
        if (!added.includes(testingMethod?.id)) {
          added.push(testingMethod.id)
          testingMethods.push(testingMethod);
        }
      }
    }
    // Sort methods by weight.
    testingMethods.sort((a, b) => a.weight - b.weight);
    return testingMethods;
  }

  return (
    <>
      {/* Layout Components */}
      {sections?.length ? (
        <div className="container px-6 pt-24 mx-auto">
          <RenderSections sections={sections} sectionMap={sectionMap}/>
        </div>
      ) : <></>}
      {/* @todo to be added back at a later date; for now postponed, do not remove. */}
      {/*{testingMethods?.length ? (*/}
      {/*  <TestingMethodsSection terms={testingMethods}/>*/}
      {/*): <></>}*/}
      {childTermsCount ? (
        <>
          <div className="container px-6 md:pt-24 md:pb-4 mx-auto scroll-m-20 text-black" ref={solutionsRef}>
            {taxonomy_term?.field_product_type_child_title ? (
              <SectionHeading title={taxonomy_term?.field_product_type_child_title}>
                <Body className="leading-relaxed font-light" bodyType={"lg"}>
                  {taxonomy_term?.field_product_type_child_desc?.processed &&
                    <FormattedText processed={taxonomy_term.field_product_type_child_desc.processed}/>}
                </Body>
              </SectionHeading>
            ) : <></>}
            <div className={classNames(
              "grid grid-cols-2 gap-0.5 md:gap-1",
              {"md:grid-cols-3": childTermsCount <= 9},
              {"md:grid-cols-4": childTermsCount > 9 && childTermsCount < 20},
              {"md:grid-cols-5": childTermsCount >= 20}
            )}>
              {childTerms.map(item => (
                <TaxonomyProductTypeTeaser taxonomy_term={item as ProductTypeTaxonomyTerm} key={item.id}
                                           className={classNames(
                                             "max-md:min-h-[120px]",
                                             {"md:h-64": childTermsCount <= 9},
                                             {"md:h-48": childTermsCount > 9 && childTermsCount < 20},
                                             {"md:h-40": childTermsCount >= 20}
                                           )}/>
              ))}
            </div>
          </div>
        </>
      ) : <></>}
      {productCount === 1 && (
        <div className="container px-6 py-24 mx-auto">
          <NodeProduct node={products[0]}/>
        </div>
      )}
      {productCount > 1 && products.length ? (
        <div className="container px-6 pt-6 md:pt-24 mx-auto text-black">
          <div className="md:mb-6" ref={solutionsRef}>
            <SectionHeading
              title={`${taxonomy_term?.field_product_type_listing_title ? taxonomy_term?.field_product_type_listing_title : t?.all?.testing_solutions}`}
              displayTitle={`${taxonomy_term?.field_product_type_listing_disp?.processed?.length ? taxonomy_term.field_product_type_listing_disp.processed : ""}`}>
                {taxonomy_term?.field_product_type_listing_desc?.processed && (
                  <FormattedText processed={taxonomy_term.field_product_type_listing_desc.processed}/>
                )}
            </SectionHeading>
          </div>
          <DynamicProductGrid nodes={products} filters={true} nodeorder={taxonomy_term?.nodeorder} paginate={3}/>
        </div>
      ) : <></>
      }
      <MissingProduct className="max-md:hidden"/>
      {additionalContent?.relatedProducts?.length ? (
        <div className="container px-6 py-24 mx-auto">
          <SectionHeading
            title={`${taxonomy_term?.field_product_type_related_title?.length ? taxonomy_term?.field_product_type_related_title : t?.all?.related_products}`}
            displayTitle={`${taxonomy_term?.field_product_type_related_disp?.processed?.length ? taxonomy_term.field_product_type_related_disp.processed : ""}`}>
            {taxonomy_term?.field_product_type_related_desc?.processed?.length ? (
              <FormattedText processed={taxonomy_term.field_product_type_related_desc.processed}/>
            ): <></>}
          </SectionHeading>
          <ProductSlider products={additionalContent?.relatedProducts}/>
        </div>
      ) : <></>}
      {additionalContent?.complementaryProducts?.length ? (
        <div className="container px-6 py-24 mx-auto">
          <SectionHeading
            title={`${taxonomy_term?.field_product_type_comp_title?.length ? taxonomy_term?.field_product_type_comp_title : t?.all?.complementary_products}`}
            displayTitle={`${taxonomy_term?.field_product_type_comp_display?.processed?.length ? taxonomy_term.field_product_type_comp_display.processed : ""}`}>
            {taxonomy_term?.field_product_type_comp_desc?.processed?.length ? (
              <FormattedText processed={taxonomy_term.field_product_type_comp_desc.processed}/>
            ): <></>}
          </SectionHeading>
          <ProductSlider products={additionalContent?.complementaryProducts}/>
        </div>
      ) : <></>}
      {taxonomy_term?.field_product_type_videos?.length ? (
        <div className="container pt-6 mx-auto">
          <div className={"mb-6"}>
            <SectionHeading title={`${t?.all?.product_videos}`}/>
          </div>
          <TrainingVideosSlider videos={taxonomy_term.field_product_type_videos ?? []} usePath={true}
                                openModalContent={setOpenModalContent} openModal={setOpenModal}/>
        </div>
      ) : <></>}
      {taxonomy_term?.faqs?.length ? (
        <div className="container px-6 pt-6 mx-auto">
          <SectionHeading
            title={`${taxonomy_term?.field_product_type_faqs_title?.length ? taxonomy_term.field_product_type_faqs_title : t?.all?.faqs}`}
            className="mb-0"/>
          {taxonomy_term.field_product_type_faqs_desc?.processed?.length ? (
            <Body className="leading-relaxed font-light" bodyType={"lg"}>
              <FormattedText processed={taxonomy_term.field_product_type_faqs_desc.processed}/>
            </Body>
          ) : <></>}
          <WidgetFaqTerms faqs={taxonomy_term.faqs}/>
        </div>
      ) : <></>}
      {additionalContent?.relatedNews?.length ? (
        <div className="container px-6 py-24 mx-auto">
          {/* @todo localize these static links */}
          <SectionHeading title={`${t?.all?.related_news}`} link={{uri: "/news", title: `${t?.all?.see_all_news}`}}/>
          <ArticleSlider articles={additionalContent?.relatedNews}/>
        </div>
      ) : <></>}
    </>
  );
}
